<template>
  <div class="log-section">
    <div v-if="logs && logs.length > 0">
      <div class="device-info">
          <div class="info-box">
            <span class="label">{{ $t('MaintenanceDeviceLogs.device_active_time') }}&nbsp;</span>
            <span class="value">{{ formatMinutes(logs[0]?.device_minutes_active ?? 0) }}</span>
          </div>
          <div class="info-box">
            <span class="label">{{ $t('MaintenanceDeviceLogs.last_received_data') }}&nbsp;</span>
            <span class="value">{{ formatMinutes(getTimeSinceLastData(logs[0]?.device_date_time ?? 0)) }}</span>
          </div>
        </div>
      <div>
        <!-- Logs Table -->
        <table v-if="logs.length > 0" class="log-table">
          <thead>
            <tr>
              <th>{{ $t('MaintenanceDeviceLogs.fw_main_controller') }}</th>
              <th>{{ $t('MaintenanceDeviceLogs.fw_weight_electronics') }}</th>
              <th>{{ $t('MaintenanceDeviceLogs.firmware_version') }}</th>
              <th>{{ $t('MaintenanceDeviceLogs.reference_height') }}</th>
              <th>{{ $t('MaintenanceDeviceLogs.system_units') }}</th>
              <th>{{ $t('MaintenanceDeviceLogs.date') }}</th>
              <th>{{ $t('MaintenanceDeviceLogs.time') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in logs" :key="log.device_time">
              <td>{{ log.device_fw_main_controller }}</td>
              <td>{{ log.device_fw_weight_electronics }}</td>
              <td>{{ log.device_sw_version }}</td>
              <td>{{ log.device_ref_height }}</td>
              <td>{{ log.device_system_units === 0 ? 'METRIC' : log.device_system_units === 1 ? 'IMPERIAL' : 'UNKNOWN' }}</td>
              <td>{{ formatLocalDate(log.device_date_time) }}</td>
              <td>{{ formatLocalTime(log.device_date_time) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="logs && logs.length > 0">
        <button @click="buttonClickShowMoreData" class="back-button">{{ $t('MaintenanceDeviceLogs.show_more') }}</button>
      </div>
    </div>
    <p v-else>{{ $t('MaintenanceDeviceLogs.no_logs_found') }}</p>
  </div>
</template>

<style scoped>
.log-section {
  width: 100%;
  max-width: 100%;
  padding: 0 20px;
  box-sizing: border-box;
}
.log-section p {
  color: #7f8c8d;
  font-style: italic;
}
th {
  text-transform: uppercase;
}

.log-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 20px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  background-color: #fff;
}
.log-table tbody tr:nth-child(even) td {
  background-color: #f9f9f9;
}
.log-table th {
  background-color: #2d8cf0;
  color: white;
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 2px solid #1c6ec7;
}
.log-table th,
.log-table td {
  width: calc(90vw / 7);
  max-width: calc(90vw / 7);
  white-space: normal;
  word-break: break-word;
  text-align: left;
  padding: 10px 12px;
}
.log-table td {
  color: #333;
  background-color: #fcfcfc;
  font-size: 20px;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s ease;
}
.log-table tr:hover {
  background-color: #f1f1f1;
}
.log-table tr:hover td {
  background-color: #f2f9ff;
}
.log-table td {
  color: #333;
}

.back-button {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  font-size: 16px;
  color: #fff;
  background-color: #179b0b;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

/* Spacing for the icon */
.back-button i {
  margin-right: 8px;
}

/* Hover effect */
.back-button:hover {
  background-color: #1f7917;
}

/* Focus effect */
.back-button:focus {
  outline: none;
}

/* Active effect */
.back-button:active {
  background-color: #1e8015;
}
.device-info {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin: 20px 0;
  flex-wrap: wrap;
}

.info-box {
  background-color: #ffffff;
  padding: 18px 26px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.04);
  border: 1px solid #e6ecf1;
  text-align: center;
  transition: box-shadow 0.2s ease;
}
.info-box:hover {
  box-shadow: 0 6px 16px rgba(0,0,0,0.08);
}

.label {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: #888;
}

.value {
  font-size: 18px;
  font-weight: bold;
  color: #1a1a1a;
}

</style>


<script>
import { backendUrl } from '../config/envVariablesConfig';
import axios from 'axios';
import keycloak from '@/keycloak';

export default {
  props: {
    deviceId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      multiplicator: 1,
      intervalId:null,
      logs: [] // Store logs fetched from API
    };
  },
  async mounted() {
    await this.fetchLogsForDevice();
    this.intervalId = setInterval(this.fetchLogsForDevice, 30000);
  },
  beforeUnmount(){
    clearInterval(this.intervalId);
  },
  watch: {
    multiplicator(newValue) {
      console.log(`Multiplicator changed to ${newValue}`);
      this.fetchLogsForDevice(); // Automatically re-fetch when multiplicator changes
    }
  },
  methods: {
    /// Emit events that trigger top level functions
    showError(message) {
      this.$emit('showError', message);
    },
    showSuccess(message) {
      this.$emit('showSuccess', message);
    },
    async buttonClickShowMoreData() {
      this.multiplicator++;
    },
    
    /**
     * Fetches logs for a specified device from the backend API.
     *
     * @returns {Promise<void>} A promise that resolves when the logs are successfully fetched and stored.
     */
    async fetchLogsForDevice() {
      try {
        // Make an API call to fetch logs
        await keycloak.updateToken(30);
        const response = await axios.get(`${backendUrl}/api/device/logs`,
          {
            params: { id: this.deviceId, multiplicator: this.multiplicator }, // Pass deviceId as a query parameter
            headers: {
              Authorization: `Bearer ${keycloak.token}`
            }
          }
        );

        // Check if the request returned any error
        if (response.status !== 200) {
          this.showError(response.data.error);
          return;
        }

        // Validate response data
        if (!response.data || !response.data.data) {
          this.showError(this.$t('general_errors.response_invalid_structure'));
          return;
        }

        if (!response.data.success) {
          this.showError(this.$t('general_errors.response_with_error'));
          return;
        }

        // Store the fetched logs in the component's state
        this.logs = response.data.data; // Assuming logs is a data property in your component
      } catch (error) {
        console.error(`fetchLogsForDevice: ${error}`); // Log the error for debugging
        this.showError(this.$t('general_errors.response_internal_error'));
      }
    },
    formatLocalDate(utcDateTime) {
      const date = new Date(utcDateTime);
      return date.toLocaleDateString("en-GB"); // "en-GB" ensures dd.mm.yyyy format
    },
    formatLocalTime(utcDateTime) {
      const date = new Date(utcDateTime);
      return date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false, // Ensures 24-hour format
      });
    },
    formatMinutes(activeTimeInMinutes) {
      const minutes = activeTimeInMinutes || 0;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${String(hours).padStart(2, '0')}h ${String(mins).padStart(2, '0')}m`;
    },
    getTimeSinceLastData(deviceDateTime) {
        if (!deviceDateTime) return 0;

        const lastTimestamp = new Date(deviceDateTime).getTime(); // Convert to milliseconds
        const now = Date.now(); // Get current timestamp in milliseconds

        const differenceInMinutes = Math.floor((now - lastTimestamp) / (1000 * 60)); // Convert ms → minutes
        return Math.max(differenceInMinutes, 0); // Ensure it never returns negative
    }
  }
};
</script>
